import { useMemo } from 'react';

import usePlaceholderItems from '../../hooks/usePlaceholderItems';
import matchesCondition from '../../lib/matchesCondition';

interface UseSurveySlidesProps {
    intro?: SurveySlide;
    form?: Form | null;
    answers?: FormSubmissionAnswer[] | null;
    errors?: { [key: string]: string[] } | null;
    routeSlideIndex?: string | null;
    basePath?: string | null;
}

export default function useSurveySlides({
    intro = null,
    form = null,
    answers = null,
    errors = null,
    routeSlideIndex = null,
    basePath = null,
}: UseSurveySlidesProps): SurveySlide[] {
    const placeholderItems = usePlaceholderItems(
        routeSlideIndex !== null ? parseInt(routeSlideIndex, 10) : 0,
    );
    return useMemo<SurveySlide[]>(() => {
        const { fields = null, results: formResults } = form || {};
        // Answers of the questions shown so far, used to evaluate the show conditions
        const visibleAnswers: Record<string, unknown> = {};
        const getAnswer = (fieldName: string) =>
            (answers || []).find(({ field: answerField }) => answerField === fieldName) || null;
        const isShown = ({ showWhen = null }: FormField) =>
            matchesCondition(showWhen, visibleAnswers);
        const makeQuestionSlide = (
            field: FormField,
            group: FormField | null = null,
        ): SurveySlide => {
            const { name: fieldName, image: fieldImage = null } = field;
            const { image: groupImage = null } = group || {};
            const answer = getAnswer(fieldName);
            if (answer !== null) {
                visibleAnswers[fieldName] = answer.value;
            }
            return {
                id: `question-${fieldName}`,
                type: 'question',
                image: groupImage || fieldImage,
                field,
                ...(group !== null ? { group } : null),
                answer,
                errors: errors !== null ? errors[fieldName] || null : null,
            };
        };
        const questionsSlides = (fields !== null ? fields : placeholderItems).reduce<SurveySlide[]>(
            (acc, field) => {
                const { type, placeholder = false } = field;
                if (placeholder) {
                    return [
                        ...acc,
                        {
                            id: `question-${acc.length}`,
                            type: 'question',
                            field: field as FormField,
                        },
                    ];
                }
                const formField = field as FormField;
                if (!isShown(formField)) {
                    return acc;
                }
                if (type === 'group') {
                    const { fields: groupFields } = formField as GroupField;
                    return groupFields.reduce<SurveySlide[]>(
                        (groupAcc, groupField) =>
                            isShown(groupField)
                                ? [...groupAcc, makeQuestionSlide(groupField, formField)]
                                : groupAcc,
                        acc,
                    );
                }
                return [...acc, makeQuestionSlide(formField)];
            },
            [],
        );
        const newSlides = [
            intro || { id: 'intro', type: 'intro', placeholder: true },
            ...questionsSlides,
            (formResults !== null
                ? {
                      ...formResults,
                      id: 'results',
                      type: 'results',
                  }
                : null) || {
                id: 'results',
                type: 'results',
                placeholder: false,
            },
        ];
        return newSlides.map((slide, index) => {
            return basePath !== null
                ? {
                      ...slide,
                      url: `${basePath}${index === 0 ? '' : `/${index}`}`,
                      nextUrl: index < newSlides.length - 1 ? `${basePath}/${index + 1}` : null,
                  }
                : slide;
        });
    }, [intro, form, answers, basePath, errors, routeSlideIndex]);
}
